/**
 * Interfaces for ng-salary component
 */

/** Form data for date range selection */
export interface SalaryFormData {
  dateFrom: Date | null;
  dateTo: Date | null;
}

/** Filter mode for customer types */
export interface CustMode {
  external: boolean;
  internal: boolean;
  absense: boolean;
}

/** Raw salary data from API response */
export interface SalaryApiResponse {
  employee_number: string;
  name: string;
  customername: string;
  project_name: string;
  activity_name: string;
  reporteddate: string;
  time: number;
  sum: number;
  tot: number;
  komp_ut: number | null;
  bonus: number;
  salary: number;
  debit: number | null;
  int_komp: number | null;
  int_proj: number | null;
  internt: number | null;
  oh: number | null;
  semester: number | null;
  sjuk: number | null;
  idle: number | null;
  tjl: number | null;
  kompbank: number;
  periodlength?: number;
  saltrans?: string[];
  // Computed fields added during processing
  diff?: number;
  mertid?: number;
  overtid?: number;
  bonusgr?: number;
  ejbonus?: number;
  bel?: number;
  totBonus?: number;
}

/** Expense group from API (type 'E') */
export interface ExpenseGroupResponse {
  expenses?: ExpenseApiItem[];
  mileages?: MileageApiItem[];
  allowances?: AllowanceApiItem[];
}

/** Base expense item from API */
interface BaseExpenseItem {
  id: number;
  employee_number: string;
  name: string;
  report_date: string;
  salary_id: number | null;
}

/** Expense (Utlägg) from API */
export interface ExpenseApiItem extends BaseExpenseItem {
  payload: ExpensePayload;
}

/** Mileage (Milersättning) from API */
export interface MileageApiItem extends BaseExpenseItem {
  payload: MileagePayload;
}

/** Allowance (Traktamente) from API */
export interface AllowanceApiItem extends BaseExpenseItem {
  payload: AllowancePayload;
}

/** Payload for expense (Utlägg) */
export interface ExpensePayload {
  expense_id: number;
  expense_date: string;
  specification: string;
  total_amount: number;
  vat_amount: number;
  expense_status: ExpenseStatus;
  images?: string[];
  receipts?: ReceiptImage[];
}

/** Payload for mileage (Milersättning) */
export interface MileagePayload {
  mileage_id: number;
  mileage_date: string;
  specification: string;
  total_amount: number;
  distance: number;
  km_reimbursement: number;
  mileage_status: ExpenseStatus;
  images?: ReceiptImage[];
}

/** Payload for allowance (Traktamente) */
export interface AllowancePayload {
  allowance_id: number;
  allowance_date?: string;
  start_date_time: string;
  end_date_time: string;
  specification: string;
  total_amount: number;
  participants?: string;
  allowance_status: ExpenseStatus;
  images?: ReceiptImage[];
}

/** Receipt image object */
export interface ReceiptImage {
  data: string;
}

/** Expense status enum */
export type ExpenseStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';

/** Union type for all payload types */
export type ExpensePayloadUnion = ExpensePayload | MileagePayload | AllowancePayload;

/** Mapped expense detail for display */
export interface ExpenseDetail {
  row: [string, string, string, string, string, number]; // [employee_number, name, date, type, spec, total]
  salary_id: number | null;
  internal_id: number;
  /** Discriminator: 'expense' = utlägg, 'mileage' = milersättning, 'allowance' = traktamente */
  outlay_type: 'expense' | 'mileage' | 'allowance';
  /** Payload without images – fetched without binary data on list load */
  payload: ExpensePayloadUnion;
}

/** Expense object for modal display */
export interface ExpenseModalData {
  specification: string;
  date: string;
  start_date_time?: string;
  end_date_time?: string;
  type: string;
  total: number;
  vat: number;
  exclVat: number;
  participants?: string;
  distance_km?: number;
  km_reimbursement?: number;
  receipts: string[];
}

/** Absence detail from API */
export interface AbsenceDetail {
  employee_number: string;
  name: string;
  reporteddate: string;
  time: number;
  salary_id: number | null;
  salary_code: string;
  record_id: number;
  customername?: string;
  project_name?: string;
  activity_name?: string;
}

/** FortNox salary transaction */
export interface FortNoxSalaryTransaction {
  EmployeeId: string;
  SalaryCode: number;
  Total: number;
  Date: string;
  TextRow: string;
  SalaryRow?: number;
}

/** FortNox absence data */
export interface FortNoxAbsenceData {
  EmployeeId: string;
  CauseCode: string;
  Date: string;
  Hours: number;
  RecordId: number;
}

/** FortNox expense data */
export interface FortNoxExpenseData {
  EmployeeId: string;
  SalaryCode: string;
  Expense: string;
  Total: number;
  Date: string;
  TextRow: string;
}

export interface InternalTimeLockSummaryPerson {
  employee_id: number;
  name: string;
  total_rows: number;
  total_hours: number;
  locked_rows: number;
  locked_hours: number;
  lockable_rows: number;
  lockable_hours: number;
}

export interface InternalTimeLockSummaryTotals {
  total_rows: number;
  total_hours: number;
  locked_rows: number;
  locked_hours: number;
  lockable_rows: number;
  lockable_hours: number;
}

/** Salary row data for bulk operations */
export interface SalaryBulkItem {
  rorlig: FortNoxSalaryTransaction;
  mert: FortNoxSalaryTransaction;
  overt: FortNoxSalaryTransaction;
}

/**
 * Processed salary row (expat array item)
 * The expat array uses numeric indices corresponding to header columns.
 * Index 23 is reserved for FortNox salary transactions.
 */
export interface ProcessedSalaryRow {
  [index: number]: string | number | FortNoxSalaryTransaction[] | null;
  // Column indices for 'T' (Tot) mode:
  // 0: ID (employee_number)
  // 1: Namn (name)
  // 2: Lön (salary)
  // 3: Bonus
  // 4: Normaltid (periodlength)
  // 5: Arbetat (tot - komp_ut)
  // 6: Komp uttag
  // 7: Sjuk
  // 8: Diff
  // 9: Bonus grund
  // 10: Ej Bonus
  // 11: Semester (days)
  // 12: Mertid
  // 13: Övertid
  // 14: Beläggning
  // 15: Sjukavdrag
  // 16: Karensavdrag
  // 17: Sem Bonus
  // 18: Lönebonus
  // 19: Tot bonus
  // 20: Mertid ers
  // 21: Övertid ers
  // 22: Kompsaldo
  // 23: salTrans (FortNoxSalaryTransaction[])
}

/** Column index constants for processed salary rows */
export const SALARY_COLUMN = {
  ID: 0,
  NAME: 1,
  SALARY: 2,
  BONUS: 3,
  NORMAL_TIME: 4,
  WORKED: 5,
  COMP_OUT: 6,
  SICK: 7,
  DIFF: 8,
  BONUS_BASE: 9,
  NO_BONUS: 10,
  VACATION: 11,
  MERTID: 12,
  OVERTID: 13,
  OCCUPANCY: 14,
  SICK_DEDUCTION: 15,
  KARENS_DEDUCTION: 16,
  VACATION_BONUS: 17,
  SALARY_BONUS: 18,
  TOTAL_BONUS: 19,
  MERTID_COMP: 20,
  OVERTID_COMP: 21,
  COMP_BALANCE: 22,
  SAL_TRANS: 23,
} as const;

/** Karka tracking array for UI state changes */
export type KarkaRow = boolean[];
